/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Injectable, ComponentFactoryResolver, ViewContainerRef, Type } from '@angular/core';

import { BehaviorSubject, Observable, concat, of } from 'rxjs';
import { map, tap, scan } from 'rxjs/operators';

import { Popup } from './Popup';
import { PopupOutput, PopupOutputType } from './interfaces';

import { PopupAnchor } from './popup-anchor.directive';
import { PopupWrapperComponent } from './popup-wrapper/popup-wrapper.component';
import { ILoggerService } from '@touchplan/logging';
import { LoggerServiceFactory } from '../logging/LoggerServiceFactory';

/**
 * # Brief
 * Display popups with arbitrary content (or simple yes/ no questions) at arbitrary
 * locations in the app. Either modal or modeless. Communication is managed via
 * observables. Configurations are largely buried behind the scenes, leaving the
 * consumer able to simply call popup.open(data). A lot of configuration available
 * in the service. To be implemented is a service function that allows for opening
 * several popups at once, only displaying one and combining the output of all of
 * them into a single stream.
 *
 *
 * ## Using a popup:
 * - import the PopupService, inject into the component you want to call open from
 * - import the popup component (SimplePopupComponent) you want to open
 * - create the popup: `let popup = this.popupService.create(SimplePopupComponent, blockBackground);`
 * - subscribe to the popup's output: `popup.output$.pipe(last()).subscribe(thing => do something)``
 *     - emits popup actions, indicating success, failure [[PopupOutput]]
 *     - observable will complete when the popup indicates it's complete, this allows
 *       for multiple emissions from a popup. Usually you won't use this
 * - open the popup: popup.open(inputData);
 *     - the type of input data depends on your popup component
 * - a popup will open at the nearest anchor point
 *
 * ## Creating an anchor point
 * - determine a place where a popup should be a anchored
 * - insert: `<ng-template popup-anchor></ng-template>`
 * - apply whatever css you need so that the popup-component that will be
 * inserted at that point is positioned appropriately. Likely you'll want it
 * wrapped in something providing centering code
 * - in the component containing the above template you'll need to create a
 * component level provider of the popupService. It is not necessary to inject it.
 *
 *
```
@Component({
    selector: 'ng2-app',
    template: `<div class="popup-wrapper-new"><ng-template popup-anchor></ng-template></div>`,
    providers: [PopupService]
})
```
 * - Every popup-anchor must correspond to exactly on instance of the popupService
 * an error will be thrown if this is violated
 *
 * ## Creating a new Popup component
 * - gen the component
 * - extend the [[PopupBaseComponent]] or implement the [[PopupCommunication]] interface
 * - if necessary create interfaces defining your inputs and output
 * - the component should be a simple block level element with no positioning information
 * that is to be entirely handled by the anchor point and the service
 * - call the writeOutput, writeCancel and complete when appropriate to the component
 * - add the component to the modules declaration and entryPoint sections
 *     - it may be more appropriate for these more custom popups to not be part of the popup module
 *       since they are "consumer" level
 *
 *
 */

// @Injectable({
// 	providedIn: 'root'//PopupModule
// })
@Injectable()
export class PopupCoreService {
	constructor(private componentFactoryResolver: ComponentFactoryResolver) {
		this.logger = LoggerServiceFactory.create('PopupCoreService');
	}

	private logger: ILoggerService;
	private _openedPopup$ = new BehaviorSubject<Popup[]>([]);
	public openedPopup$ = this._openedPopup$.asObservable();
	public isOpened$ = this.openedPopup$.pipe(map((list) => !!list.length));

	public create(anchor: PopupAnchor, component: Type<any>, blockBackground = false) {
		const componentFactory = this.componentFactoryResolver.resolveComponentFactory(component);
		const viewContainerRef = anchor.viewContainerRef;

		// this.closeAllPopups();

		const list = [...this._openedPopup$.getValue()];
		this.logger.log('block', blockBackground);
		let wrapFn;
		if (blockBackground) {
			wrapFn = (vcr) => this.wrapPopup(vcr);
		} else {
			wrapFn = (vcr) => vcr;
		}
		const popup = new Popup(viewContainerRef, componentFactory, wrapFn);

		list.push(popup);
		this._openedPopup$.next(list);
		// this.openedPopups.push(popup);

		popup.output$.subscribe({
			complete: () => {
				this.logger.log('popup output completed');
				const list = [...this._openedPopup$.getValue()];

				const idx = list.indexOf(popup);
				if (idx !== -1) {
					list.splice(idx, 1);
					this._openedPopup$.next(list);
				}
			},
		});

		return popup;
	}

	/**
	 * TODO - need to determine implementation
	 * should multi-ing happen automatically as a result of opening multiple popups, or make it only explicit?
	 * should be multi-wrapper component be fully in charge of managing the popups or should the service
	 * could always just pass in the popups and let it do it's own thing
	 * also, should it support having different wrapper components being specificed
	 * should it support switching between the popups
	 * ----------------
	 * Start with super lazy api, can swap out with better version later
	 *
	 * @deprecated
	 */
	public openMultiPopup(
		popups: Popup[],
		openData: any[],
		abortOnAnyCancel = true
	): Observable<PopupOutput[]> {
		function closeWhatsLeft(idx) {
			for (let i = idx + 1; i < popups.length; i++) {
				popups[i].close();
			}
		}

		if (!popups.length) {
			return of([]);
		}

		const execution = [];
		popups.forEach((p, i) => {
			execution.push(
				new Observable((subscriber) => {
					p.open(openData[i]);
					subscriber.complete();
				})
			);
			execution.push(p.output$.pipe(map((thing) => [thing, i])));
		});
		return concat(...execution).pipe(
			tap((out: [PopupOutput, number]) => {
				if (abortOnAnyCancel && out[0].type === PopupOutputType.canceled) {
					closeWhatsLeft(out[1]);
				}
			}),
			scan(
				(acc: any[], val) => {
					const newAcc = [...acc];
					newAcc[val[1]] = val[0];
					return newAcc;
				},
				popups.map((p) => null)
			)
		);
	}

	//to be passed into the popup so the service can be updated when the popup is updated
	// provides a revised injection point based on how the popup was created
	// TODO - look into if nested views need to be cleaned up at all
	private wrapPopup(viewContainerRef: ViewContainerRef) {
		const wrapperComponent = this.componentFactoryResolver.resolveComponentFactory(PopupWrapperComponent);
		const wrapperRef = viewContainerRef.createComponent(wrapperComponent);
		return wrapperRef.instance.insertion;
	}

	public closeAllPopups() {
		this._openedPopup$.getValue().forEach((p) => p.close());
	}
}
